import React, { useCallback, useMemo, useRef, useState } from 'react';
import { StyledProfileFooterFlexContainer } from '../../pages/SpecialistProfile/SpecialistProfile.style';
import { FormattedMessage } from 'react-intl';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  IconButton,
  IconChatFilled,
  Skeleton,
  Specialist,
  Typography,
  TypographyVariantsEnum,
  useSpecialistScheduleContext,
} from '@mindly/ui-components';
import { LoadPaymentModal } from '../../modals/LoadPaymentModal';
import { useAppointment } from '../../hooks/useAppointment/useAppointment';
import { useSelector } from 'react-redux';
import { ImpactStyle } from '@capacitor/haptics';
import { useTranslation } from 'react-i18next';
import { isLoadingSpecialistByIDSelector, psychologistSelectorById } from 'src/redux/specialists/selector';
import { AppointmentFooterProps } from './types';
import { useIsClientCan } from 'src/shared/hooks/useIsClientCan';
import { useChat } from 'src/shared/hooks/useChat';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import ConfirmAppointmentFeature from '../../features/ConfirmAppointmentFeature';
import { ConfirmAppointmentFeatureRef } from '../../features/ConfirmAppointmentFeature/types';

const AppointmentFooter: React.FC<AppointmentFooterProps> = ({
  loading,
  specialistID,
  countConsultationFor3Days,
  specialistFullName,
  presentingElement,
}) => {
  const { handleChatClick } = useChat();

  const { isPushToAppointmentLoading } = useAppointment();
  const { t } = useTranslation(['specialists', 'ui', 'app']);
  const isBookingLoading = useSelector(isLoadingSpecialistByIDSelector);
  const [isBookSlotLoading, setIsBookSlotLoading] = useState(false);
  const specialist = useSelector(psychologistSelectorById(specialistID));
  const { selectedSlot } = useSpecialistScheduleContext();
  const confirmAppointmentRef = useRef<ConfirmAppointmentFeatureRef>(null);

  const isLoading = useMemo(
    () => isBookSlotLoading || isBookingLoading || loading,
    [isBookSlotLoading, isBookingLoading, loading]
  );

  const { isClientCan } = useIsClientCan(specialistID);
  const isClientCanBookSession = isClientCan({
    action: 'book_session',
  });

  const handleBookingClick = useCallback(async () => {
    confirmAppointmentRef.current?.handleBookingClick();
  }, []);

  return (
    <AppFooter borderTop={false}>
      {!loading ? (
        <div>
          {!!countConsultationFor3Days && (
            <Typography variant={TypographyVariantsEnum.Text13Regular} align="center" className="mb-4">
              {t('ui:therapist_details.sale_hint', { count: countConsultationFor3Days })}
            </Typography>
          )}

          {isClientCanBookSession ? (
            <div className="flex items-center gap-x-5">
              <IconButton
                fill="filled"
                icon={<IconChatFilled />}
                onClick={() => {
                  isomorficHapticsImpact({ style: ImpactStyle.Light });
                  handleChatClick(specialistID, presentingElement);
                }}
              />
              <Button
                fullWidth
                data-testid="appointment-button"
                disabled={loading}
                isLoading={isLoading}
                size="large"
                className="flex-1"
                onClick={handleBookingClick}
              >
                <FormattedMessage id="specialist.profile.signIn" defaultMessage="Записатися" />
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center flex-1">
              <Typography
                variant={TypographyVariantsEnum.Text14Regular}
                align="center"
                className="!text-typography-typography-primary/50 mb-5"
              >
                {t('therapist_details.closed_booking_hint', {
                  name: specialistFullName,
                })}
              </Typography>
              <Button
                fullWidth
                data-testid="appointment-button"
                disabled={loading}
                size="large"
                iconPosition="center"
                icon={<IconChatFilled />}
                onClick={() => {
                  isomorficHapticsImpact({ style: ImpactStyle.Light });
                  handleChatClick(specialistID, presentingElement);
                }}
              >
                {t('therapist_details.chat_button')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <StyledProfileFooterFlexContainer>
          <Skeleton heightPx={48} />
          <Skeleton heightPx={48} />
        </StyledProfileFooterFlexContainer>
      )}
      <LoadPaymentModal
        fetchingStatus={isPushToAppointmentLoading ? 'start' : null}
        paymentStatus={null}
        loadType={'checkout'}
      />
      {specialist && (
        <ConfirmAppointmentFeature
          ref={confirmAppointmentRef}
          specialist={specialist as unknown as Specialist}
          selectedSlot={selectedSlot}
          setIsBookSlotLoading={setIsBookSlotLoading}
        />
      )}
    </AppFooter>
  );
};

export default React.memo(AppointmentFooter);
